"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type CopyButtonProps = {
    textToCopy: string;
    label?: string;
    className?: string;
};

export default function CopyButton({
    textToCopy,
    label = "copy",
    className,
}: CopyButtonProps) {
    const [icon, setIcon] = useState(label);
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        setIcon(label);
    }, [label]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(textToCopy);
        } catch {
            // Ignore clipboard errors to keep UI clean.
        }
        setIcon("copied");
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
            setIcon(label);
        }, 1600);
    }, [label, textToCopy]);

    return (
        <button
            type="button"
            className={twMerge(
                "inline-flex items-center bg-transparent p-0 font-inherit text-inherit outline-none focus:outline-none focus-visible:outline-none",
                className,
            )}
            onClick={handleCopy}
            aria-label="Copy install command"
        >
            {icon}
        </button>
    );
}
